import { api } from "@/services/auth";

export type ComercialOpportunity = {
  id: string;
  empresa: string;
  tipo: string;
  etapa: string;
  etapaRaw?: RawOpportunityStage;
  temperatura: string;
  responsavel: string;
  diasNoEstagio: number;
  processos: number;
};

export type ComercialModuleSummary = {
  modulo: string;
  count: number;
  ativos: number;
  href: string;
};

export type ComercialDashboardData = {
  kpis: {
    oportunidadesAtivas: number;
    taxaConversao: string;
    tempoMedioConversao: string;
    emAtendimento: number;
    emNegociacao: number;
    negociosFechados: number;
    negociosPerdidos: number;
    novosLeadsMes: number;
    aguardandoFormalizacao: number;
    clientesVinculados: number;
  };
  oportunidades: ComercialOpportunity[];
  modulos: ComercialModuleSummary[];
  origemLeads: Array<{ source: string; pct: number; count: number }>;
};

export type ComercialFunnelSeriesPoint = {
  period: string;
  leads: number;
  conversions: number;
};

export type ComercialOpportunityItem = {
  id: string;
  title: string;
  description: string | null;
  stage: string;
  status: string;
  domain: string;
  client_id: string | null;
  client_name?: string | null;
  process_id: string | null;
  owner_name: string | null;
  estimated_value: string | null;
  currency: string | null;
  sla_days: number;
  expected_close_date: string | null;
  lost_reason: string | null;
  won_at: string | null;
  lost_at: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  stagnated?: boolean;
};

export type ComercialOpportunityListResponse = {
  page: number;
  pageSize: number;
  total: number;
  items: ComercialOpportunityItem[];
};

export type GroupedNegotiationStage = "em-negociacao";
export type ClientOpportunityStage = "lead" | GroupedNegotiationStage | "ganho" | "perdido";
export type RawOpportunityStage = "lead" | "qualificacao" | "proposta" | "negociacao" | "ganho" | "perdido";
export type OpportunityStageFilter = RawOpportunityStage | ClientOpportunityStage;

const RAW_OPPORTUNITY_STAGES: RawOpportunityStage[] = ["lead", "qualificacao", "proposta", "negociacao", "ganho", "perdido"];
const NEGOTIATION_RAW_OPPORTUNITY_STAGES: RawOpportunityStage[] = ["qualificacao", "proposta", "negociacao"];
type CreateOportunidadePayload = {
  title: string;
  stage?: string;
  value_estimate?: number;
  estimatedValue?: number;
  probability?: number;
  client_id?: string;
  clientId?: string;
  owner_user_id?: string;
  ownerName?: string;
  description?: string;
  domain?: string;
  metadata?: Record<string, unknown>;
};

// Empty state — no fake data
const fallbackData: ComercialDashboardData = {
  kpis: {
    oportunidadesAtivas: 0,
    taxaConversao: "0%",
    tempoMedioConversao: "—",
    emAtendimento: 0,
    emNegociacao: 0,
    negociosFechados: 0,
    negociosPerdidos: 0,
    novosLeadsMes: 0,
    aguardandoFormalizacao: 0,
    clientesVinculados: 0,
  },
  oportunidades: [],
  modulos: [],
  origemLeads: [],
};

type ComercialDashboardApiResponse = {
  counters?: {
    open_total?: number;
    lead?: number;
    qualificacao?: number;
    proposta?: number;
    negociacao?: number;
    ganho?: number;
    perdido?: number;
    stagnated?: number;
  };
  aging?: {
    up_to_7d?: number;
    from_7_to_15d?: number;
    from_15_to_30d?: number;
    above_30d?: number;
  };
  opportunities?: {
    items?: Array<{
      id: string;
      title?: string;
      stage?: string;
      domain?: string;
      owner_name?: string | null;
      updated_at?: string | null;
      stagnated?: boolean;
      client_id?: string | null;
      process_id?: string | null;
    }>;
  };
};

export function mapDomainToTipo(domain?: string) {
  if (domain === "marcas") return "Marca";
  if (domain === "patentes") return "Patente";
  if (domain === "di") return "DI";
  return "Internacional";
}

function normalizeStageComparableText(stage?: string) {
  return String(stage || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function normalizeOpportunityStageFilter(stage?: string): OpportunityStageFilter | undefined {
  const rawStage = normalizeOpportunityRawStage(stage);
  if (rawStage) return rawStage;
  return normalizeOpportunityClientStage(stage);
}

export function normalizeOpportunityRawStage(stage?: string): RawOpportunityStage | undefined {
  const normalized = normalizeStageComparableText(stage).replace(/[_\s]+/g, "-");
  if (!normalized) return undefined;
  if (normalized === "atendimento") return "lead";
  if (normalized === "fechado" || normalized === "fechados") return "ganho";
  if (normalized === "perdidos") return "perdido";
  const rawStage = normalized.replace(/-/g, "");
  if (RAW_OPPORTUNITY_STAGES.includes(rawStage as RawOpportunityStage)) {
    return rawStage as RawOpportunityStage;
  }
  return undefined;
}

function normalizeOpportunityClientStage(stage?: string): ClientOpportunityStage | undefined {
  const normalized = normalizeStageComparableText(stage).replace(/[_\s]+/g, "-");
  if (!normalized) return undefined;
  if (normalized === "atendimento") return "lead";
  if (normalized === "em-negociacao") return "em-negociacao";
  if (normalized === "negocios-fechados" || normalized === "fechado" || normalized === "fechados") return "ganho";
  if (normalized === "negocios-perdidos" || normalized === "perdidos") return "perdido";
  if (normalized === "lead" || normalized === "ganho" || normalized === "perdido") {
    return normalized as Exclude<ClientOpportunityStage, GroupedNegotiationStage>;
  }
  return undefined;
}

export function mapOpportunityClientStage(stage?: string): ClientOpportunityStage {
  const rawStage = normalizeOpportunityRawStage(stage);
  if (rawStage === "qualificacao" || rawStage === "proposta" || rawStage === "negociacao") return "em-negociacao";
  if (rawStage === "ganho") return "ganho";
  if (rawStage === "perdido") return "perdido";
  const clientStage = normalizeOpportunityClientStage(stage);
  if (clientStage) return clientStage;
  return "lead";
}

export function resolveOpportunityStageUpdate(currentStage?: string, nextStage?: string): RawOpportunityStage | undefined {
  const normalizedNextStage = normalizeOpportunityRawStage(nextStage);
  if (normalizedNextStage) return normalizedNextStage;

  const clientNextStage = normalizeOpportunityClientStage(nextStage);
  if (!clientNextStage) return undefined;
  if (clientNextStage !== "em-negociacao") {
    return normalizeOpportunityRawStage(clientNextStage);
  }

  const normalizedCurrentStage = normalizeOpportunityRawStage(currentStage);
  if (
    normalizedCurrentStage &&
    NEGOTIATION_RAW_OPPORTUNITY_STAGES.includes(normalizedCurrentStage)
  ) {
    return normalizedCurrentStage;
  }

  return "qualificacao";
}

export function mapOpportunityStageLabel(stage?: string) {
  const clientStage = mapOpportunityClientStage(stage);
  if (clientStage === "em-negociacao") return "Em Negociação";
  if (clientStage === "ganho") return "Negócios Fechados";
  if (clientStage === "perdido") return "Negócios Perdidos";
  return "Atendimento";
}

export function mapOpportunityExactStageLabel(stage?: string) {
  const rawStage = normalizeOpportunityRawStage(stage);
  if (rawStage === "qualificacao") return "Qualificação";
  if (rawStage === "proposta") return "Proposta";
  if (rawStage === "negociacao") return "Negociação";
  if (rawStage === "ganho") return "Negócios Fechados";
  if (rawStage === "perdido") return "Negócios Perdidos";
  if (normalizeOpportunityClientStage(stage) === "em-negociacao") return "Em Negociação";
  return "Atendimento";
}

export function buildOpportunityDetailPath(id: string, stage?: string) {
  const basePath = `/comercial/atendimentos/${id}`;
  const rawStage = normalizeOpportunityRawStage(stage);
  if (rawStage) return `${basePath}?etapa=${rawStage}`;
  const clientStage = normalizeOpportunityClientStage(stage);
  if (clientStage) return `${basePath}?etapa=${clientStage}`;
  if (!String(stage || "").trim()) return basePath;
  return `${basePath}?etapa=${mapOpportunityClientStage(stage)}`;
}

function countItemsByClientStage(
  items: NonNullable<ComercialDashboardApiResponse["opportunities"]>["items"],
  stage: ClientOpportunityStage
) {
  return (items || []).filter((item) => mapOpportunityClientStage(item.stage) === stage).length;
}

function resolveCounterValue(rawValue: number | undefined, fallback: number) {
  return rawValue == null ? fallback : Number(rawValue || 0);
}

function mapTemperature(item: { stage?: string; stagnated?: boolean }) {
  if (item.stagnated) return "Fria";
  const clientStage = mapOpportunityClientStage(item.stage);
  if (clientStage === "ganho") return "Quente";
  if (clientStage === "em-negociacao") {
    if (item.stage === "proposta" || item.stage === "qualificacao") return "Morna";
    return "Quente";
  }
  return "Fria";
}

function diffDays(updatedAt?: string | null) {
  if (!updatedAt) return 0;
  const updated = new Date(updatedAt).getTime();
  if (Number.isNaN(updated)) return 0;
  return Math.max(0, Math.floor((Date.now() - updated) / 86400000));
}

function adaptDashboard(payload: unknown): ComercialDashboardData {
  if (!payload || typeof payload !== "object") return fallbackData;
  const obj = payload as Partial<ComercialDashboardData>;
  const apiObj = payload as ComercialDashboardApiResponse;
  if (apiObj.counters || apiObj.opportunities) {
    const counters = apiObj.counters || {};
    const opportunitiesRaw = Array.isArray(apiObj.opportunities?.items) ? apiObj.opportunities?.items : [];
    const oportunidades = opportunitiesRaw.map((item) => ({
      id: String(item.id),
      empresa: String(item.title || `Oportunidade ${item.id}`),
      tipo: mapDomainToTipo(item.domain),
      etapa: mapOpportunityStageLabel(item.stage),
      etapaRaw: normalizeOpportunityRawStage(item.stage),
      temperatura: mapTemperature(item),
      responsavel: String(item.owner_name || "Equipe Comercial"),
      diasNoEstagio: diffDays(item.updated_at),
      processos: item.process_id ? 1 : 0,
    }));
    const ganho = resolveCounterValue(counters.ganho, countItemsByClientStage(opportunitiesRaw, "ganho"));
    const perdido = resolveCounterValue(counters.perdido, countItemsByClientStage(opportunitiesRaw, "perdido"));
    const emAtendimento = resolveCounterValue(counters.lead, countItemsByClientStage(opportunitiesRaw, "lead"));
    const emNegociacao =
      counters.qualificacao != null || counters.proposta != null || counters.negociacao != null
        ? Number(counters.qualificacao || 0) + Number(counters.proposta || 0) + Number(counters.negociacao || 0)
        : countItemsByClientStage(opportunitiesRaw, "em-negociacao");
    const totalFechados = ganho + perdido;
    const taxaConversao = totalFechados > 0 ? `${Math.round((ganho / totalFechados) * 100)}%` : "0%";
    const uniqueClients = new Set(opportunitiesRaw.map((item) => item.client_id).filter(Boolean)).size;
    const domainBuckets = new Map<string, { modulo: string; count: number; ativos: number; href: string }>();
    for (const item of opportunitiesRaw) {
      const domain = String(item.domain || "patentes");
      const modulo = mapDomainToTipo(domain) === "Marca" ? "Marcas" : mapDomainToTipo(domain) === "Patente" ? "Patentes" : mapDomainToTipo(domain);
      const href = domain === "marcas" ? "/marcas" : domain === "di" ? "/di" : domain === "internacional" ? "/internacional" : "/patentes";
      const existing = domainBuckets.get(domain) || { modulo, count: 0, ativos: 0, href };
      existing.count += 1;
      if (item.stage !== "perdido") existing.ativos += 1;
      domainBuckets.set(domain, existing);
    }
    const modulos = Array.from(domainBuckets.values());
    const aging = apiObj.aging || {};
    const origemLeads = [
      { source: "Até 7 dias", count: Number(aging.up_to_7d || 0), pct: 0 },
      { source: "7 a 15 dias", count: Number(aging.from_7_to_15d || 0), pct: 0 },
      { source: "15 a 30 dias", count: Number(aging.from_15_to_30d || 0), pct: 0 },
      { source: "Acima de 30 dias", count: Number(aging.above_30d || 0), pct: 0 },
    ];
    const totalOrigem = origemLeads.reduce((acc, item) => acc + item.count, 0);
    for (const item of origemLeads) {
      item.pct = totalOrigem > 0 ? Math.round((item.count / totalOrigem) * 100) : 0;
    }
    return {
      kpis: {
        oportunidadesAtivas: Number(counters.open_total || 0),
        taxaConversao,
        tempoMedioConversao: `${Math.max(1, Math.round((Number(aging.up_to_7d || 0) * 5 + Number(aging.from_7_to_15d || 0) * 11 + Number(aging.from_15_to_30d || 0) * 22 + Number(aging.above_30d || 0) * 35) / Math.max(1, totalOrigem || 1)))} dias`,
        emAtendimento,
        emNegociacao,
        negociosFechados: ganho,
        negociosPerdidos: perdido,
        novosLeadsMes: Number(counters.lead || 0),
        aguardandoFormalizacao: emNegociacao,
        clientesVinculados: uniqueClients,
      },
      oportunidades: oportunidades.length > 0 ? oportunidades : fallbackData.oportunidades,
      modulos: modulos.length > 0 ? modulos : fallbackData.modulos,
      origemLeads,
    };
  }
  return {
    kpis: {
      oportunidadesAtivas: Number(obj.kpis?.oportunidadesAtivas ?? fallbackData.kpis.oportunidadesAtivas),
      taxaConversao: String(obj.kpis?.taxaConversao ?? fallbackData.kpis.taxaConversao),
      tempoMedioConversao: String(obj.kpis?.tempoMedioConversao ?? fallbackData.kpis.tempoMedioConversao),
      emAtendimento: Number(obj.kpis?.emAtendimento ?? fallbackData.kpis.emAtendimento),
      emNegociacao: Number(obj.kpis?.emNegociacao ?? fallbackData.kpis.emNegociacao),
      negociosFechados: Number(obj.kpis?.negociosFechados ?? fallbackData.kpis.negociosFechados),
      negociosPerdidos: Number(obj.kpis?.negociosPerdidos ?? fallbackData.kpis.negociosPerdidos),
      novosLeadsMes: Number(obj.kpis?.novosLeadsMes ?? fallbackData.kpis.novosLeadsMes),
      aguardandoFormalizacao: Number(obj.kpis?.aguardandoFormalizacao ?? fallbackData.kpis.aguardandoFormalizacao),
      clientesVinculados: Number(obj.kpis?.clientesVinculados ?? fallbackData.kpis.clientesVinculados),
    },
    oportunidades: Array.isArray(obj.oportunidades) ? obj.oportunidades : fallbackData.oportunidades,
    modulos: Array.isArray(obj.modulos) ? obj.modulos : fallbackData.modulos,
    origemLeads: Array.isArray(obj.origemLeads) ? obj.origemLeads : fallbackData.origemLeads,
  };
}

function adaptFunnelSeries(payload: unknown): ComercialFunnelSeriesPoint[] {
  const rawSeries =
    payload && typeof payload === "object" && Array.isArray((payload as { series?: unknown[] }).series)
      ? (payload as { series: unknown[] }).series
      : [];

  return rawSeries
    .map((item) => {
      const point = (item && typeof item === "object" ? item : {}) as Partial<ComercialFunnelSeriesPoint>;
      return {
        period: String(point.period || ""),
        leads: Number(point.leads || 0),
        conversions: Number(point.conversions || 0),
      };
    })
    .filter((item) => item.period);
}

export const comercialService = {
  getFallback: (): ComercialDashboardData => fallbackData,
  listOportunidades: async (params?: { page?: number; pageSize?: number; stage?: string; status?: string; all_branches?: boolean; client_id?: string; owner?: string; q?: string }) => {
    const { data } = await api.get<ComercialOpportunityListResponse>("/v2/comercial/oportunidades", { params });
    return data;
  },
  getOportunidade: async (id: string) => {
    const { data } = await api.get<ComercialOpportunityItem>(`/v2/comercial/oportunidades/${id}`);
    return data;
  },
  updateOportunidade: async (id: string, payload: Partial<ComercialOpportunityItem> & { stage?: string }) => {
    const { data } = await api.patch<{ id: string; updated: boolean }>(`/v2/comercial/oportunidades/${id}`, payload);
    return data;
  },
  createOportunidade: async (payload: CreateOportunidadePayload) => {
    const normalizedPayload = {
      ...payload,
      estimatedValue: payload.estimatedValue ?? payload.value_estimate,
      clientId: payload.clientId ?? payload.client_id,
    };
    const { data } = await api.post<{ id: string }>("/v2/comercial/oportunidades", normalizedPayload);
    return data;
  },
  getDashboard: async (): Promise<ComercialDashboardData> => {
    try {
      const { data } = await api.get("/v2/comercial/dashboard");
      return adaptDashboard(data);
    } catch {
      return fallbackData;
    }
  },
  getFunnelSeries: async (): Promise<ComercialFunnelSeriesPoint[]> => {
    try {
      const { data } = await api.get("/v2/comercial/funnel-history");
      return adaptFunnelSeries(data);
    } catch {
      return [];
    }
  },
};
